import {
  IWFNodes,
  IWfBounds,
  IWfCollabOp,
  IWfElement,
  IWfPoint,
  IWorkFlow,
  parsePointer,
  removeAtPath,
  setAtPath
} from "@kuika/kuika-cl-model";
import { IWfRegistryElement } from "./workflow-doc-extractor";

/**
 * Remote workflow op batch → LIVE bpmn-js canvas + selectedWf side-car (Faz 4b, pragmatic
 * v1 — correctness over elegance).
 *
 * The convergence source of truth is the shadow/live IWfDocument the session core applies
 * ops to via `applyWfOps`; this module is the MATERIALIZATION step that makes the visible
 * canvas and the semantic side-car follow. Op kinds it can map confidently are applied
 * granularly through `modeling` / `elementFactory` / `bpmnFactory`; anything else returns
 * `{ needsResync: true }` and the owning provider falls back to a FULL RESYNC from the
 * authoritative envelope (GET .../state → importXML + selectedWf replace + viewbox
 * preserve). Full resync on hard ops is explicitly acceptable for v1 — silent divergence
 * is not.
 *
 * Granular vs resync (the honest table):
 *  - wfGeometrySet      → resizeShape / updateWaypoints / label moveShape (granular)
 *  - wfFlowEndpointsSet → reconnectStart / reconnectEnd (granular)
 *  - wfPropSet /name    → modeling.updateProperties name + semantic node (granular)
 *  - wfPropSet/Remove   → semantic node via sd-path (granular, no canvas change)
 *  - wfDocPropSet/Remove→ selectedWf parameters/happyPathDefinition/docExtras (granular);
 *                         "/participantBounds" → pool resizeShape (KOA-18258 — the pool is
 *                         doc-level geometry, not an elements-map entry)
 *  - wfLaneDerived      → doc-only laneId (no canvas/side-car materialization in v1)
 *  - wfElementRemove    → removeElements + side-car prune (granular)
 *  - wfElementUpsert    → INSERT of a plain task/event/gateway shape or a sequence flow
 *                         (granular); REPLACE of an existing element, boundary events,
 *                         subprocesses, call activities, lanes, or event-definition
 *                         carriers → RESYNC
 *  - wfContainerSet     → same-container reorder is XML-emission-only (no canvas action);
 *                         container CHANGE (subprocess containment move) → RESYNC
 *  - wfAttachSet        → boundary re-host → RESYNC
 *
 * An op whose target vanished locally (racing our own unacked delete) is a NO-OP, not a
 * resync — mirroring the wf-patch applier's skip-not-throw semantics.
 *
 * Every call runs inside the owning provider's isApplyingRemote guard so the bpmn command
 * stack events it triggers are never re-captured as local ops.
 */

// ---- envelope ({"xml": "...", "json": "..."} — the wire format of every workflow snapshot) ----

export interface IWfCollabEnvelope {
  xml: string;
  json: string;
}

export function buildWfEnvelope(xml: string, json: string): string {
  return JSON.stringify({ xml, json });
}

export function parseWfEnvelope(raw: string): IWfCollabEnvelope | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return null;
    const candidate: IWfCollabEnvelope = parsed as IWfCollabEnvelope;
    if (typeof candidate.xml !== "string" || candidate.xml === "") return null;
    if (typeof candidate.json !== "string") return null;
    return { xml: candidate.xml, json: candidate.json };
  } catch {
    return null;
  }
}

// ---- narrow structural views of the bpmn-js editing surfaces ---------------------------------

export interface IWfShapeDelta {
  x: number;
  y: number;
}

export interface IWfModelingLike {
  resizeShape(shape: IWfRegistryElement, bounds: IWfBounds): void;
  moveShape(shape: IWfRegistryElement, delta: IWfShapeDelta): void;
  updateWaypoints(connection: IWfRegistryElement, waypoints: IWfPoint[]): void;
  reconnectStart(connection: IWfRegistryElement, newSource: IWfRegistryElement, dockingPoint: IWfPoint): void;
  reconnectEnd(connection: IWfRegistryElement, newTarget: IWfRegistryElement, dockingPoint: IWfPoint): void;
  removeElements(elements: IWfRegistryElement[]): void;
  createShape(shape: unknown, position: IWfPoint, target: IWfRegistryElement): IWfRegistryElement;
  createConnection(
    source: IWfRegistryElement,
    target: IWfRegistryElement,
    attrs: IWfCreateConnectionAttrs,
    parent: IWfRegistryElement
  ): IWfRegistryElement;
  updateProperties(element: IWfRegistryElement, properties: IWfUpdatableProperties): void;
}

export interface IWfUpdatableProperties {
  name?: string | undefined;
}

export interface IWfCreateShapeAttrs {
  type: string;
  businessObject: unknown;
  width?: number;
  height?: number;
}

export interface IWfCreateConnectionAttrs {
  type: string;
  businessObject: unknown;
}

export interface IWfElementFactoryLike {
  createShape(attrs: IWfCreateShapeAttrs): unknown;
}

export interface IWfBpmnFactoryLike {
  create(type: string, attrs: IWfBusinessObjectSeed): unknown;
}

export interface IWfBusinessObjectSeed {
  id: string;
  name?: string;
}

export interface IWfElementRegistryReader {
  get(id: string): IWfRegistryElement | undefined;
  getAll(): IWfRegistryElement[];
}

export interface IWfSyncServiceLike {
  syncElement(element: IWfRegistryElement, workflow: IWorkFlow, currentTargetWorkflow?: unknown): void;
  deleteElement(element: IWfRegistryElement, workflow: IWorkFlow, currentTargetWorkflow?: unknown): void;
}

export interface IWfCanvasSurfaces {
  modeling: IWfModelingLike;
  elementFactory: IWfElementFactoryLike;
  bpmnFactory: IWfBpmnFactoryLike;
  elementRegistry: IWfElementRegistryReader;
  syncService: IWfSyncServiceLike;
}

export interface IWfRemoteApplyResult {
  needsResync: boolean;
  /** First op that forced the resync — telemetry only. */
  resyncOpId?: string;
  resyncReason?: string;
}

// ---- op kind support tables --------------------------------------------------------------------

/** Shape kinds a wfElementUpsert INSERT can materialize confidently (no event definition). */
const SIMPLE_INSERT_SHAPE_TYPES: ReadonlySet<string> = new Set<string>([
  "bpmn:Task",
  "bpmn:UserTask",
  "bpmn:SendTask",
  "bpmn:StartEvent",
  "bpmn:EndEvent",
  "bpmn:IntermediateCatchEvent",
  "bpmn:ExclusiveGateway",
  "bpmn:ParallelGateway",
  "bpmn:InclusiveGateway"
]);

/** Semantic node keys that are derived on the wire — a wfPropSet must never write them. */
const DERIVED_SEMANTIC_ROOTS: ReadonlySet<string> = new Set<string>([
  "id",
  "name",
  "nodeType",
  "stepType",
  "boundaryEvent",
  "subprocess",
  "eventType",
  "eventTag",
  "isInterrupting",
  "gateWayType",
  "arrowType",
  "fromNodeId",
  "toNodeId",
  "isDefault"
]);

const WF_DOC_PROP_ROOTS: ReadonlySet<string> = new Set<string>([
  "name",
  "parameters",
  "happyPathDefinition",
  "docExtras"
]);

// ---- semantic side-car lookup --------------------------------------------------------------------

interface IWfSemanticNode {
  id?: string;
  name?: string;
  boundaryEvent?: IWfSemanticNode;
  subprocess?: IWFNodes;
  [key: string]: unknown;
}

interface IWfSemanticBag {
  steps?: IWfSemanticNode[];
  events?: IWfSemanticNode[];
  gateways?: IWfSemanticNode[];
  lanes?: IWfSemanticNode[];
  forwards?: IWfSemanticNode[];
  decisions?: IWfSemanticNode[];
}

/** Recursive by-id lookup over the side-car (steps/events/gateways/lanes/flows + boundary + subprocess). */
export function findWfSemanticNode(workflow: IWorkFlow | undefined, elementId: string): IWfSemanticNode | undefined {
  const search = (bag: IWfSemanticBag | undefined): IWfSemanticNode | undefined => {
    if (!bag) return undefined;
    const lists: (IWfSemanticNode[] | undefined)[] = [
      bag.steps,
      bag.events,
      bag.gateways,
      bag.lanes,
      bag.forwards,
      bag.decisions
    ];
    for (const list of lists) {
      const found: IWfSemanticNode | undefined = list?.find((node: IWfSemanticNode) => node?.id === elementId);
      if (found) return found;
    }
    for (const step of bag.steps ?? []) {
      if (step.boundaryEvent?.id === elementId) return step.boundaryEvent;
      if (step.subprocess) {
        const nested: IWfSemanticNode | undefined = search(step.subprocess as unknown as IWfSemanticBag);
        if (nested) return nested;
      }
    }
    return undefined;
  };
  return search(workflow?.nodes as unknown as IWfSemanticBag | undefined);
}

function cloneJsonClean<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

// ---- per-kind materializers ------------------------------------------------------------------------

type WfMaterializeOutcome = "applied" | "noop" | "resync";

function centerOf(element: IWfRegistryElement): IWfPoint {
  return {
    x: (element.x ?? 0) + (element.width ?? 0) / 2,
    y: (element.y ?? 0) + (element.height ?? 0) / 2
  };
}

/**
 * wfDocPropSet "/participantBounds" → resize the pool shape (KOA-18258). The participant
 * is not an elements-map entry, so its geometry arrives as a doc-level op; without this
 * materialization the receiving canvas keeps a pool desynced from its lanes (the exact
 * corruption the vocabulary extension exists to prevent). No side-car field mirrors it.
 */
function applyParticipantBounds(surfaces: IWfCanvasSurfaces, op: IWfCollabOp): WfMaterializeOutcome {
  const participant: IWfRegistryElement | undefined = surfaces.elementRegistry
    .getAll()
    .find((element: IWfRegistryElement): boolean => element.type === "bpmn:Participant");
  if (!participant) return "noop"; // no pool on this canvas — the doc-level field already converged
  const value: unknown = op.value;
  if (typeof value !== "object" || value === null || Array.isArray(value)) return "noop";
  const candidate: Partial<IWfBounds> = value as Partial<IWfBounds>;
  if (
    typeof candidate.x !== "number" ||
    typeof candidate.y !== "number" ||
    typeof candidate.width !== "number" ||
    typeof candidate.height !== "number"
  ) {
    return "noop"; // the server applier rejects malformed bounds — defensive skip only
  }
  const sameBounds: boolean =
    participant.x === candidate.x &&
    participant.y === candidate.y &&
    participant.width === candidate.width &&
    participant.height === candidate.height;
  if (sameBounds) return "applied";
  surfaces.modeling.resizeShape(participant, {
    x: candidate.x,
    y: candidate.y,
    width: candidate.width,
    height: candidate.height
  });
  return "applied";
}

function applyGeometry(surfaces: IWfCanvasSurfaces, op: IWfCollabOp): WfMaterializeOutcome {
  const element: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.elementId ?? "");
  if (!element) return "noop"; // racing our own unacked delete — skip, like the doc applier
  if (op.bounds !== undefined) {
    surfaces.modeling.resizeShape(element, {
      x: op.bounds.x,
      y: op.bounds.y,
      width: op.bounds.width,
      height: op.bounds.height
    });
  }
  if (op.waypoints !== undefined) {
    surfaces.modeling.updateWaypoints(
      element,
      op.waypoints.map((point: IWfPoint): IWfPoint => ({ x: point.x, y: point.y }))
    );
  }
  if (op.labelBounds !== undefined && op.labelBounds !== null) {
    const label: IWfRegistryElement | undefined = element.label;
    if (label && typeof label.x === "number" && typeof label.y === "number") {
      const delta: IWfShapeDelta = { x: op.labelBounds.x - label.x, y: op.labelBounds.y - label.y };
      if (delta.x !== 0 || delta.y !== 0) {
        surfaces.modeling.moveShape(label, delta);
      }
    }
    // No live label shape: cosmetic only — the doc already converged; never resync for it.
  }
  return "applied";
}

function applyFlowEndpoints(surfaces: IWfCanvasSurfaces, op: IWfCollabOp): WfMaterializeOutcome {
  const connection: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.elementId ?? "");
  if (!connection) return "noop";
  const newSource: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.sourceId ?? "");
  const newTarget: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.targetId ?? "");
  if (!newSource || !newTarget) return "noop"; // endpoint racing a local delete
  if (connection.source?.id !== newSource.id) {
    surfaces.modeling.reconnectStart(connection, newSource, centerOf(newSource));
  }
  if (connection.target?.id !== newTarget.id) {
    surfaces.modeling.reconnectEnd(connection, newTarget, centerOf(newTarget));
  }
  return "applied";
}

function applyRemove(surfaces: IWfCanvasSurfaces, selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  const ids: string[] = [op.elementId ?? "", ...(op.cascadedIds ?? [])].filter((id: string) => id !== "");
  const present: IWfRegistryElement[] = [];
  for (const id of ids) {
    const element: IWfRegistryElement | undefined = surfaces.elementRegistry.get(id);
    if (element) present.push(element);
  }
  // Side-car prune BEFORE canvas removal — deleteElement routes by the businessObject
  // parent chain, which bpmn-js unlinks during removeElements.
  for (const element of present) {
    surfaces.syncService.deleteElement(element, selectedWf);
  }
  if (present.length > 0) {
    surfaces.modeling.removeElements(present);
  }
  return "applied"; // remove of an already-gone element is an accepted no-op (both deleters win)
}

function overlayUpsertSemantics(selectedWf: IWorkFlow, record: IWfElement): void {
  const node: IWfSemanticNode | undefined = findWfSemanticNode(selectedWf, record.id);
  if (!node) return;
  const semantics: Record<string, unknown> = record.semantics ?? {};
  for (const key of Object.keys(semantics)) {
    if (DERIVED_SEMANTIC_ROOTS.has(key)) continue;
    node[key] = cloneJsonClean(semantics[key]);
  }
  if (typeof record.name === "string") {
    node.name = record.name;
  }
}

function applyUpsert(surfaces: IWfCanvasSurfaces, selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  const record: IWfElement | undefined = op.element;
  if (!record || typeof record.id !== "string" || record.id === "") return "noop";
  const existing: IWfRegistryElement | undefined = surfaces.elementRegistry.get(record.id);
  if (existing) {
    // REPLACE: a whole-record upsert can change fields (bpmnType, passthrough XML,
    // event definitions, ...) v1 cannot re-materialize confidently.
    return "resync";
  }
  const parent: IWfRegistryElement | undefined = resolveCanvasParent(surfaces, record.containerId);
  if (!parent) return "resync";

  if (record.bpmnType === "bpmn:SequenceFlow") {
    const source: IWfRegistryElement | undefined = surfaces.elementRegistry.get(record.sourceId ?? "");
    const target: IWfRegistryElement | undefined = surfaces.elementRegistry.get(record.targetId ?? "");
    // Endpoints genuinely absent. NOTE the doc applier does NOT skip this op — it happily
    // stores a flow with a (temporarily) dangling endpoint — so a silent no-op here is a
    // canvas/doc divergence. applyWfOpsToCanvas therefore DEFERS flow inserts whose
    // endpoints are missing to the end of the batch (KOA-18191: an undo batch restored
    // the flow before the node it attaches to); by the time we are called with missing
    // endpoints, the retry already happened and the endpoints are truly gone (a racing
    // concurrent delete) — the next diff/resync converges that case.
    if (!source || !target) return "noop";
    const seed: IWfBusinessObjectSeed = { id: record.id };
    if (typeof record.name === "string") seed.name = record.name;
    const businessObject: unknown = surfaces.bpmnFactory.create("bpmn:SequenceFlow", seed);
    const connection: IWfRegistryElement = surfaces.modeling.createConnection(
      source,
      target,
      { type: "bpmn:SequenceFlow", businessObject },
      parent
    );
    if (record.waypoints !== undefined && record.waypoints.length >= 2) {
      surfaces.modeling.updateWaypoints(
        connection,
        record.waypoints.map((point: IWfPoint): IWfPoint => ({ x: point.x, y: point.y }))
      );
    }
    surfaces.syncService.syncElement(connection, selectedWf);
    overlayUpsertSemantics(selectedWf, record);
    return "applied";
  }

  const hasEventDefinition: boolean = record.eventDefinition !== undefined && record.eventDefinition !== "none";
  if (!SIMPLE_INSERT_SHAPE_TYPES.has(record.bpmnType) || hasEventDefinition || record.bounds === undefined) {
    // Boundary events, subprocesses, call activities, lanes, event-definition carriers,
    // or a shape without geometry — v1 materializes none of these confidently.
    return "resync";
  }

  const seed: IWfBusinessObjectSeed = { id: record.id };
  if (typeof record.name === "string") seed.name = record.name;
  const businessObject: unknown = surfaces.bpmnFactory.create(record.bpmnType, seed);
  const shape: unknown = surfaces.elementFactory.createShape({
    type: record.bpmnType,
    businessObject,
    width: record.bounds.width,
    height: record.bounds.height
  });
  const created: IWfRegistryElement = surfaces.modeling.createShape(
    shape,
    { x: record.bounds.x + record.bounds.width / 2, y: record.bounds.y + record.bounds.height / 2 },
    parent
  );
  surfaces.syncService.syncElement(created, selectedWf);
  overlayUpsertSemantics(selectedWf, record);
  return "applied";
}

/** Canvas parent for containerId: "" → participant/process root; else the subprocess shape. */
function resolveCanvasParent(
  surfaces: IWfCanvasSurfaces,
  containerId: string | undefined
): IWfRegistryElement | undefined {
  if (containerId !== undefined && containerId !== "") {
    const subprocess: IWfRegistryElement | undefined = surfaces.elementRegistry.get(containerId);
    if (subprocess?.type === "bpmn:SubProcess") return subprocess;
    return undefined;
  }
  const all: IWfRegistryElement[] = surfaces.elementRegistry.getAll();
  const participant: IWfRegistryElement | undefined = all.find(
    (element: IWfRegistryElement) => element.type === "bpmn:Participant"
  );
  if (participant) return participant;
  return all.find((element: IWfRegistryElement) => element.type === "bpmn:Process");
}

function applyContainerSet(surfaces: IWfCanvasSurfaces, op: IWfCollabOp): WfMaterializeOutcome {
  const element: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.elementId ?? "");
  if (!element) return "noop";
  const currentContainerId: string = currentCanvasContainerId(element);
  if ((op.containerId ?? "") === currentContainerId) {
    // Same-container reorder: pure XML-emission ordering — the doc apply already
    // converged it; the canvas has no visual order to update.
    return "applied";
  }
  return "resync"; // real containment move (into/out of a subprocess)
}

function currentCanvasContainerId(element: IWfRegistryElement): string {
  let current: IWfRegistryElement | undefined = element.parent;
  while (current) {
    if (current.type === "bpmn:SubProcess") return current.id;
    if (
      current.type === "bpmn:Participant" ||
      current.type === "bpmn:Process" ||
      current.type === "bpmn:Collaboration"
    ) {
      return "";
    }
    current = current.parent;
  }
  return "";
}

function applyPropSet(surfaces: IWfCanvasSurfaces, selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  const path: string = op.path ?? "";
  const segments: readonly { kind: string; key?: string }[] | null = parsePointer(path);
  if (!segments || segments.length === 0) return "noop";
  if (segments[0].kind === "plain" && segments[0].key === "name") {
    if (segments.length !== 1 || typeof op.value !== "string") return "noop"; // BAD_PATH/BAD_OP on the doc side too
    const element: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.elementId ?? "");
    if (element) {
      surfaces.modeling.updateProperties(element, { name: op.value });
    }
    const node: IWfSemanticNode | undefined = findWfSemanticNode(selectedWf, op.elementId ?? "");
    if (node) node.name = op.value;
    return "applied";
  }
  if (segments[0].kind === "plain" && DERIVED_SEMANTIC_ROOTS.has(segments[0].key ?? "")) {
    return "noop"; // a compliant differ never targets derived keys — refuse silently
  }
  const node: IWfSemanticNode | undefined = findWfSemanticNode(selectedWf, op.elementId ?? "");
  if (!node) return "noop";
  setAtPath(node, path, cloneJsonClean(op.value));
  return "applied";
}

function applyPropRemove(surfaces: IWfCanvasSurfaces, selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  const path: string = op.path ?? "";
  const segments: readonly { kind: string; key?: string }[] | null = parsePointer(path);
  if (!segments || segments.length === 0) return "noop";
  if (segments[0].kind === "plain" && segments[0].key === "name") {
    if (segments.length !== 1) return "noop";
    const element: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.elementId ?? "");
    if (element) {
      surfaces.modeling.updateProperties(element, { name: undefined });
    }
    const node: IWfSemanticNode | undefined = findWfSemanticNode(selectedWf, op.elementId ?? "");
    if (node) delete node.name;
    return "applied";
  }
  if (segments[0].kind === "plain" && DERIVED_SEMANTIC_ROOTS.has(segments[0].key ?? "")) {
    return "noop";
  }
  const node: IWfSemanticNode | undefined = findWfSemanticNode(selectedWf, op.elementId ?? "");
  if (!node) return "noop";
  removeAtPath(node, path);
  return "applied";
}

interface IWfDocPropWrapper {
  parameters?: unknown;
  happyPathDefinition?: unknown;
  docExtras?: Record<string, unknown>;
}

function applyDocPropSet(selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  const path: string = op.path ?? "";
  const segments: readonly { kind: string; key?: string }[] | null = parsePointer(path);
  if (!segments || segments.length === 0 || segments[0].kind !== "plain") return "noop";
  const root: string = segments[0].key ?? "";
  if (!WF_DOC_PROP_ROOTS.has(root)) return "noop";
  if (root === "name") return "applied"; // side-car carries no root name (server RootExtras) — doc-only
  if (segments.length === 1) {
    if (root === "parameters") {
      selectedWf.parameters = cloneJsonClean(op.value) as IWorkFlow["parameters"];
      return "applied";
    }
    if (root === "happyPathDefinition") {
      selectedWf.happyPathDefinition = cloneJsonClean(op.value) as IWorkFlow["happyPathDefinition"];
      return "applied";
    }
    // docExtras whole-set: replace the nodes bag's non-array fields.
    const bag: Record<string, unknown> | undefined = selectedWf.nodes as unknown as Record<string, unknown> | undefined;
    if (!bag || typeof op.value !== "object" || op.value === null || Array.isArray(op.value)) return "noop";
    const next: Record<string, unknown> = op.value as Record<string, unknown>;
    for (const key of Object.keys(bag)) {
      if (!Array.isArray(bag[key]) && next[key] === undefined) delete bag[key];
    }
    for (const key of Object.keys(next)) {
      bag[key] = cloneJsonClean(next[key]);
    }
    return "applied";
  }
  const wrapper: IWfDocPropWrapper = {
    parameters: selectedWf.parameters,
    happyPathDefinition: selectedWf.happyPathDefinition,
    docExtras: selectedWf.nodes as unknown as Record<string, unknown> | undefined
  };
  if ((wrapper as Record<string, unknown>)[root] === undefined) return "noop"; // deep set under a missing base
  setAtPath(wrapper, path, cloneJsonClean(op.value));
  return "applied";
}

function applyDocPropRemove(selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  const path: string = op.path ?? "";
  const segments: readonly { kind: string; key?: string }[] | null = parsePointer(path);
  if (!segments || segments.length === 0 || segments[0].kind !== "plain") return "noop";
  const root: string = segments[0].key ?? "";
  if (!WF_DOC_PROP_ROOTS.has(root)) return "noop";
  if (root === "name") return "applied";
  if (segments.length === 1) {
    if (root === "parameters") {
      delete selectedWf.parameters;
      return "applied";
    }
    if (root === "happyPathDefinition") {
      delete selectedWf.happyPathDefinition;
      return "applied";
    }
    const bag: Record<string, unknown> | undefined = selectedWf.nodes as unknown as Record<string, unknown> | undefined;
    if (!bag) return "noop";
    for (const key of Object.keys(bag)) {
      if (!Array.isArray(bag[key])) delete bag[key];
    }
    return "applied";
  }
  const wrapper: IWfDocPropWrapper = {
    parameters: selectedWf.parameters,
    happyPathDefinition: selectedWf.happyPathDefinition,
    docExtras: selectedWf.nodes as unknown as Record<string, unknown> | undefined
  };
  if ((wrapper as Record<string, unknown>)[root] === undefined) return "noop";
  removeAtPath(wrapper, path);
  return "applied";
}

// ---- the batch applier -------------------------------------------------------------------------------

/**
 * bpmn:Group (happy-path phase) ops always converge via full resync: materializing them
 * through modeling.* commands would fire the happyPathPhaseInterceptor's command-stack
 * listeners (strict tiling, retile cascades, definition sync saves) mid-remote-apply,
 * while importXML applies the authoritative layout with the interceptor silent. The
 * happyPathDefinition side-car itself still applies granularly via wfDocPropSet.
 */
function targetsGroup(surfaces: IWfCanvasSurfaces, op: IWfCollabOp): boolean {
  if (op.kind === "wfElementUpsert") return op.element?.bpmnType === "bpmn:Group";
  if (op.kind === "wfDocPropSet" || op.kind === "wfDocPropRemove" || op.kind === "wfLaneDerived") return false;
  if (typeof op.elementId !== "string" || op.elementId === "") return false;
  const existing: IWfRegistryElement | undefined = surfaces.elementRegistry.get(op.elementId);
  return existing?.type === "bpmn:Group";
}

function materializeOne(surfaces: IWfCanvasSurfaces, selectedWf: IWorkFlow, op: IWfCollabOp): WfMaterializeOutcome {
  if (targetsGroup(surfaces, op)) return "resync";
  switch (op.kind) {
    case "wfGeometrySet":
      return applyGeometry(surfaces, op);
    case "wfFlowEndpointsSet":
      return applyFlowEndpoints(surfaces, op);
    case "wfElementRemove":
      return applyRemove(surfaces, selectedWf, op);
    case "wfElementUpsert":
      return applyUpsert(surfaces, selectedWf, op);
    case "wfContainerSet":
      return applyContainerSet(surfaces, op);
    case "wfAttachSet":
      return "resync"; // boundary re-host — not confidently mappable in v1
    case "wfPropSet":
      return applyPropSet(surfaces, selectedWf, op);
    case "wfPropRemove":
      return applyPropRemove(surfaces, selectedWf, op);
    case "wfDocPropSet":
      if (op.path === "/participantBounds") {
        return applyParticipantBounds(surfaces, op);
      }
      return applyDocPropSet(selectedWf, op);
    case "wfDocPropRemove":
      return applyDocPropRemove(selectedWf, op);
    case "wfLaneDerived":
      return "applied"; // doc-only field; the canvas derives lane visuals from geometry
    default:
      return "resync"; // unknown kind — a newer peer speaks a vocabulary we don't; converge from authority
  }
}

/**
 * Materializes a remote (or ack-synthetic, or undo/redo-replayed) op batch onto the live
 * canvas + side-car. Continues past no-ops; stops at the FIRST op that demands a resync —
 * the caller then discards granular state and reloads the authoritative envelope, so
 * partially-applied prefixes are safe.
 */
export function applyWfOpsToCanvas(
  surfaces: IWfCanvasSurfaces,
  selectedWf: IWorkFlow,
  ops: IWfCollabOp[]
): IWfRemoteApplyResult {
  // KOA-18191: an undo batch can carry a sequence-flow INSERT before the node it attaches
  // to (the inverse of a cascade delete replays in reverse-remove order). The doc applier
  // accepts the temporarily-dangling flow, but the canvas insert needs both endpoints in
  // the registry — so flow inserts whose endpoints are not there YET are deferred to the
  // end of the batch and retried once everything else (the node upserts) has landed.
  const deferredFlowInserts: IWfCollabOp[] = [];
  const materialize = (op: IWfCollabOp): IWfRemoteApplyResult | null => {
    let outcome: WfMaterializeOutcome;
    try {
      outcome = materializeOne(surfaces, selectedWf, op);
    } catch {
      // A modeling call blew up mid-materialization — never leave the canvas half-applied.
      return { needsResync: true, resyncOpId: op.opId, resyncReason: `materialize threw on ${op.kind}` };
    }
    if (outcome === "resync") {
      return { needsResync: true, resyncOpId: op.opId, resyncReason: op.kind };
    }
    return null;
  };

  for (const op of ops) {
    if (isFlowInsertAwaitingEndpoints(surfaces, op)) {
      deferredFlowInserts.push(op);
      continue;
    }
    const failure: IWfRemoteApplyResult | null = materialize(op);
    if (failure) return failure;
  }
  for (const op of deferredFlowInserts) {
    const failure: IWfRemoteApplyResult | null = materialize(op);
    if (failure) return failure;
  }
  return { needsResync: false };
}

/** A sequence-flow INSERT whose endpoints are not in the registry yet — deferrable (KOA-18191). */
function isFlowInsertAwaitingEndpoints(surfaces: IWfCanvasSurfaces, op: IWfCollabOp): boolean {
  if (op.kind !== "wfElementUpsert") return false;
  const record: IWfElement | undefined = op.element;
  if (!record || record.bpmnType !== "bpmn:SequenceFlow" || typeof record.id !== "string") return false;
  if (surfaces.elementRegistry.get(record.id)) return false; // replace path, not an insert
  const source: IWfRegistryElement | undefined = surfaces.elementRegistry.get(record.sourceId ?? "");
  const target: IWfRegistryElement | undefined = surfaces.elementRegistry.get(record.targetId ?? "");
  return !source || !target;
}
